import { getIconUrl } from "@/lib/directus";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { motion } from "framer-motion";
import { useState } from "react";
import TextButton from "../ui/TextButton";
import Button, { ButtonType } from "../reusable/Button";

export interface CardProps {
  image: string;
  image_link?: string;
  buttonText: string;
  title: string;
  alt?: string;
  text: string;
  viewAllButton: string;
  viewAllLink: string;
  boxHeight?: string;
}

// Custom smooth scroll funkcija (isti kao u Button.tsx)
function smoothScrollTo(element: HTMLElement, duration = 1200) {
  const targetPosition =
    element.getBoundingClientRect().top + window.pageYOffset;
  const startPosition = window.pageYOffset;
  const distance = targetPosition - startPosition;
  let startTime: number | null = null;

  function animation(currentTime: number) {
    if (startTime === null) startTime = currentTime;
    const timeElapsed = currentTime - startTime;
    const run = ease(timeElapsed, startPosition, distance, duration);
    window.scrollTo(0, run);
    if (timeElapsed < duration) requestAnimationFrame(animation);
  }

  function ease(t: number, b: number, c: number, d: number) {
    t /= d / 2;
    if (t < 1) return (c / 2) * t * t * t + b;
    t -= 2;
    return (c / 2) * (t * t * t + 2) + b;
  }

  requestAnimationFrame(animation);
}

export default function Card({
  image,
  buttonText,
  title,
  text,
  viewAllButton,
  viewAllLink,
  image_link,
  alt,
  boxHeight,
}: CardProps) {
  const [isTextVisible, setIsTextVisible] = useState(false);

  const isSmall = boxHeight === "h-[50vh]";

  const useCloudinary = image_link && image_link.length > 0;

  const imageSrc = useCloudinary ? image_link : getIconUrl(image);
  const altImage = useCloudinary ? alt : title;

  let href = viewAllLink;
  if (viewAllLink && viewAllLink.startsWith("{slug}")) {
    href = viewAllLink.replace("{slug}", "");
  }

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (viewAllLink && viewAllLink.startsWith("#")) {
      e.preventDefault();
      const id = viewAllLink.slice(1);
      const el = document.getElementById(id);
      if (el) {
        smoothScrollTo(el, 1200);
      }
    }
  };

  return (
    <div
      className={`relative group w-full ${boxHeight} overflow-hidden bg-accent-color-three-dark rounded-none shadow-lg flex flex-col justify-end`}
      onMouseEnter={() => setIsTextVisible(true)}
      onMouseLeave={() => setIsTextVisible(false)}
    >
      <Image
        src={imageSrc}
        alt={altImage ?? title}
        fill
        className="object-cover"
        sizes="320px"
        priority={false}
        style={{ zIndex: 0 }}
      />

      <motion.div
        className="absolute left-0 right-0 bottom-8 flex justify-center pointer-events-none"
        initial={{ opacity: 1 }}
        animate={isTextVisible ? { opacity: 0 } : { opacity: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        style={{ zIndex: 10 }}
      >
        <div className="pointer-events-auto">
          <Button
            type={isSmall ? ButtonType.secondary : ButtonType.primary}
            link={viewAllLink}
            title={buttonText}
          />
        </div>
      </motion.div>

      {!isSmall ? (
        <div
          className={cn(
            "absolute inset-0 bottom-0 top-[auto] flex flex-col gap-16 justify-end bg-accent-color-three-dark transition-opacity duration-300 py-16 px-[7.5%]",
            {
              "opacity-100 z-20": isTextVisible,
              "opacity-0 z-[-1]": !isTextVisible,
            }
          )}
        >
          <motion.h3
            className="font-serif text-secondary-light text-h2-desktop"
            initial={{ opacity: 0 }}
            animate={isTextVisible ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
          >
            {title}
          </motion.h3>
          <motion.p
            className="font-sans text-white text-lg"
            initial={{ opacity: 0 }}
            animate={isTextVisible ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.25 }}
          >
            {text}
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={isTextVisible ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.4 }}
          >
            <TextButton
              text={viewAllButton}
              link={viewAllLink}
              textColor="primary"
            />
          </motion.div>
        </div>
      ) : (
        <div
          className={cn(
            "absolute inset-0 flex flex-col  transition-opacity duration-300 p-[3.33vw] justify-center items-start gap-4",
            {
              "opacity-100 z-20": isTextVisible,
              "opacity-0 z-[-1]": !isTextVisible,
            }
          )}
          style={{
            backgroundColor: isTextVisible ? "#273240" : "transparent",
            color: "#fff",
          }}
        >
          <motion.h3
            className="text-white text-4xl font-sans mb-8"
            initial={{ opacity: 0 }}
            animate={isTextVisible ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
          >
            {title.toUpperCase()}
          </motion.h3>
          <motion.p
            className={cn(
              "text-white font-sans mb-4",
              isSmall ? "text-small" : "text-lg"
            )}
            initial={{ opacity: 0 }}
            animate={isTextVisible ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 0.18 }}
          >
            {text}
          </motion.p>
          <motion.div
            className="w-full flex"
            initial={{ opacity: 0 }}
            animate={isTextVisible ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 0.25 }}
          >
            <Button
              type={ButtonType.outlineLight}
              link={viewAllLink}
              title={viewAllButton}
              arrow
              className="py-2"
            />
          </motion.div>
        </div>
      )}
    </div>
  );
}
